import { useEffect, useRef } from "react";
import { locations } from "@/lib/locations-data";
import { useDeferUntilInteraction } from "@/lib/defer";
import "leaflet/dist/leaflet.css";

const mainLocation = locations[0];

const LocationMap = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  // Leaflet + OpenStreetMap tiles are heavy; only load them once the visitor
  // interacts (the map sits below the fold on every page that uses it).
  const ready = useDeferUntilInteraction();

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    const initMap = async () => {
      const L = await import("leaflet");
      if (cancelled) return;

      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
      }

      if (!mapRef.current) return;

      const map = L.map(mapRef.current, {
        zoomControl: false,
        scrollWheelZoom: false,
        dragging: true,
      }).setView([mainLocation.lat, mainLocation.lng], 10);

      mapInstanceRef.current = map;

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
        maxZoom: 19,
      }).addTo(map);

      // 30-mile radius circle
      L.circle([mainLocation.lat, mainLocation.lng], {
        radius: 48280,
        color: "hsl(152, 52%, 33%)",
        fillColor: "hsl(152, 52%, 33%)",
        fillOpacity: 0.08,
        weight: 2,
        dashArray: "8 4",
      }).addTo(map);

      const primaryIcon = L.divIcon({
        className: "custom-marker-primary",
        html: `<div style="background:hsl(152, 52%, 33%);width:32px;height:32px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/></svg>
        </div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
      });

      const cityIcon = L.divIcon({
        className: "custom-marker-city",
        html: `<div style="background:hsl(34,80%,52%);width:18px;height:18px;border-radius:50%;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.2);"></div>`,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      });

      const detailHref = `/locations/${mainLocation.slug}`;
      const viewDetailsBtn = `
        <a href="${detailHref}" style="
          display:inline-flex;align-items:center;gap:6px;margin-top:10px;
          padding:7px 12px;border-radius:8px;
          background:hsl(152, 52%, 33%);color:white;
          font-size:12px;font-weight:600;text-decoration:none;
          box-shadow:0 4px 12px -2px rgba(20,80,45,0.35);
        ">
          View Details
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
        </a>`;

      // Main location marker
      L.marker([mainLocation.lat, mainLocation.lng], { icon: primaryIcon })
        .addTo(map)
        .bindPopup(
          `<div style="padding:8px;font-family:sans-serif;min-width:160px;">
            <strong style="font-size:14px;">${mainLocation.name}</strong>
            <p style="color:#666;font-size:12px;margin:4px 0 0;">Main Service Area</p>
            ${viewDetailsBtn}
          </div>`
        );

      // City markers
      mainLocation.cities.forEach((city) => {
        L.marker([city.lat, city.lng], { icon: cityIcon })
          .addTo(map)
          .bindPopup(
            `<div style="padding:6px;font-family:sans-serif;min-width:160px;">
              <strong style="font-size:13px;">${city.name}</strong>
              <p style="color:#666;font-size:11px;margin:2px 0 0;">Within 30-mile radius</p>
              ${viewDetailsBtn}
            </div>`
          );
      });
    };

    initMap();

    return () => {
      cancelled = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [ready]);

  return (
    <div className="relative w-full h-[520px] md:h-[600px] lg:h-[680px] overflow-hidden" style={{ zIndex: 0 }}>
      <div ref={mapRef} role="region" aria-label="Service area map" className="w-full h-full" style={{ zIndex: 0 }} />
      {/* Bottom gradient fade so the overlay panel reads cleanly */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-72 z-[400]"
        style={{ background: "linear-gradient(to bottom, transparent 0%, hsl(var(--background) / 0.85) 60%, hsl(var(--background)) 100%)" }}
      />
    </div>
  );
};

export default LocationMap;
